// Intel panel (GDD §14): a fixed HUD panel that shows the card (intel.ts) for what the pointer or keyboard focus is
// on — the in-game replacement for browser tooltips. Idle, it says what it is for. Rows are reused, so showing a card
// allocates nothing once the panel has held its largest card.

import { el, TextSlot } from './dom'
import type { InfoCard, InfoTone } from './intel'

const IDLE_KICKER = 'INTEL'
const IDLE_TITLE = 'Nothing selected'
const IDLE_NOTE = 'Point at a unit, strike, building or city for its details.'

const TONE_CLASSES: Readonly<Record<InfoTone, string>> = {
  plain: '',
  good: 'is-good',
  bad: 'is-bad',
  money: 'is-money',
  player: 'is-player',
  enemy: 'is-enemy',
  neutral: 'is-neutral',
}

interface RowSlot {
  readonly label: HTMLElement
  readonly value: HTMLElement
  readonly labelText: TextSlot
  readonly valueText: TextSlot
  tone: InfoTone
}

export class InfoPanel {
  readonly element: HTMLElement
  private readonly kicker: TextSlot
  private readonly title: TextSlot
  private readonly table: HTMLElement
  private readonly note: TextSlot
  private readonly footer: TextSlot
  private readonly rows: RowSlot[] = []

  constructor() {
    this.element = el('section', 'tw-intel tw-panel')
    this.element.setAttribute('aria-label', 'Intel')
    const kicker = el('p', 'tw-intel__kicker')
    const title = el('h2', 'tw-intel__title')
    this.table = el('dl', 'tw-intel__rows')
    const note = el('p', 'tw-intel__note')
    const footer = el('p', 'tw-intel__footer')
    this.element.append(kicker, title, this.table, note, footer)
    this.kicker = new TextSlot(kicker)
    this.title = new TextSlot(title)
    this.note = new TextSlot(note)
    this.footer = new TextSlot(footer)
    this.clear()
  }

  show(card: InfoCard): void {
    this.element.classList.remove('is-idle')
    this.kicker.set(card.kicker)
    this.title.set(card.title)
    const rows = card.rows
    while (this.rows.length < rows.length) this.rows.push(this.addRow())
    for (let i = 0; i < this.rows.length; i++) {
      const slot = this.rows[i] as RowSlot
      const content = rows[i]
      const shown = content !== undefined
      slot.label.hidden = !shown
      slot.value.hidden = !shown
      if (content === undefined) continue
      slot.labelText.set(content.label)
      slot.valueText.set(content.value)
      this.setTone(slot, content.tone)
    }
    this.note.set(card.note ?? '')
    this.footer.set(card.footer ?? '')
  }

  /** Back to the idle panel. */
  clear(): void {
    this.element.classList.add('is-idle')
    this.kicker.set(IDLE_KICKER)
    this.title.set(IDLE_TITLE)
    for (const slot of this.rows) {
      slot.label.hidden = true
      slot.value.hidden = true
    }
    this.note.set(IDLE_NOTE)
    this.footer.set('')
  }

  private addRow(): RowSlot {
    const label = el('dt', 'tw-intel__label')
    const value = el('dd', 'tw-intel__value')
    this.table.append(label, value)
    return { label, value, labelText: new TextSlot(label), valueText: new TextSlot(value), tone: 'plain' }
  }

  private setTone(slot: RowSlot, tone: InfoTone): void {
    if (slot.tone === tone) return
    const previous = TONE_CLASSES[slot.tone]
    const next = TONE_CLASSES[tone]
    if (previous !== '') slot.value.classList.remove(previous)
    if (next !== '') slot.value.classList.add(next)
    slot.tone = tone
  }
}
